<template>
  <footer class="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
    <!-- Background Pattern -->
    <div class="absolute inset-0 opacity-30">
      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-accent/5 to-transparent" />
    </div>

    <!-- Floating Elements -->
    <div class="absolute left-10 top-10 size-16 animate-float rounded-full bg-gradient-to-r from-accent/10 to-gradient-end/10 blur-xl" />
    <div class="absolute right-20 top-20 size-24 animate-float rounded-full bg-gradient-to-r from-gradient-start/10 to-accent/10 blur-2xl" style="animation-delay: 2s;" />
    <div class="absolute bottom-20 left-1/4 size-12 animate-float rounded-full bg-gradient-to-r from-gradient-end/10 to-accent/10 blur-lg" style="animation-delay: 4s;" />

    <div class="container relative mx-auto max-w-7xl px-6">
      <div class="border-t border-gray-200/50 py-16 dark:border-gray-700/50">
        <!-- Main Footer Content -->
        <div class="mb-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-5">
          <!-- Company Info -->
          <div class="lg:col-span-2">
            <div class="mb-6">
              <h3 class="mb-4 text-2xl font-bold text-ternary-dark dark:text-primary-light">
                Vedware
              </h3>
              <p class="mb-6 max-w-md text-gray-600 dark:text-gray-400">
                Full-stack web development company specializing in custom applications, modern frontends, and scalable backend solutions.
              </p>

              <!-- Social Links -->
              <div class="mb-6">
                <h4 class="mb-4 text-lg font-semibold text-ternary-dark dark:text-ternary-light">
                  Connect With Us
                </h4>
                <div class="flex gap-4">
                  <a
                    v-for="social in socialProfiles"
                    :key="social.name"
                    :href="social.url"
                    :title="social.name"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="group flex size-12 items-center justify-center rounded-full bg-gradient-to-r from-accent/10 to-gradient-end/10 transition-all duration-300 hover:-translate-y-1 hover:from-accent hover:to-gradient-end hover:shadow-lg dark:from-accent/20 dark:to-gradient-end/20 dark:hover:from-accent dark:hover:to-gradient-end"
                  >
                    <component :is="social.icon" class="size-5 text-gray-600 transition-colors duration-300 group-hover:text-white dark:text-gray-400" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Links -->
          <div>
            <h4 class="mb-6 text-lg font-semibold text-ternary-dark dark:text-ternary-light">
              Quick Links
            </h4>
            <ul class="space-y-3">
              <li>
                <NuxtLink to="/" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Home
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/about" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  About Us
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/projects" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Projects
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/services" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Services
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/contact" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Contact
                </NuxtLink>
              </li>
            </ul>
          </div>

          <!-- Services -->
          <div>
            <h4 class="mb-6 text-lg font-semibold text-ternary-dark dark:text-ternary-light">
              Services
            </h4>
            <ul class="space-y-3">
              <li>
                <NuxtLink to="/services/web-development" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Web Development
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/services/frontend-development" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Frontend Development
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/services/backend-development" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Backend Development
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/services/api-development" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  API Development
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/services/mobile-app-development" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Mobile App Development
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/services/devops-deployment" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  DevOps & Deployment
                </NuxtLink>
              </li>
            </ul>
          </div>

          <!-- Team -->
          <div>
            <h4 class="mb-6 text-lg font-semibold text-ternary-dark dark:text-ternary-light">
              Our Team
            </h4>
            <ul class="space-y-3">
              <li>
                <NuxtLink to="/about" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  About Us
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/about/vedmant" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Arthur - Lead Developer
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/about/dasha" class="text-gray-600 transition-colors duration-300 hover:text-accent dark:text-gray-400 dark:hover:text-accent">
                  Dasha - Project Manager
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>

        <!-- Footer Copyright -->
        <FooterCopyright />
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { PhEnvelope, PhGithubLogo, PhLinkedinLogo, PhQuestion } from '@phosphor-icons/vue'
import FooterCopyright from './FooterCopyright.vue'

interface SocialProfile {
  name: string
  icon: any
  url: string
}

const _copyrightDate = new Date().getFullYear()
const socialProfiles: SocialProfile[] = [
  {
    name: 'GitHub',
    icon: markRaw(PhGithubLogo),
    url: 'https://github.com/vedmant',
  },
  {
    name: 'LinkedIn',
    icon: markRaw(PhLinkedinLogo),
    url: 'https://www.linkedin.com/in/anatolii-v-25a86690/',
  },
  {
    name: 'Stack Overflow',
    icon: markRaw(PhQuestion),
    url: 'https://stackoverflow.com/users/1753349/vedmant',
  },
  {
    name: 'Email',
    icon: markRaw(PhEnvelope),
    url: 'mailto:info@vedware.com',
  },
]
</script>
